import { Box, Center, Spinner, useDisclosure } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { TakeoverConfirmationModal } from './conversation-takeover-confirmation-modal';
import { MessageInput } from './conversation-message-input';
import { MessageThread } from './conversation-message-thread';
import { UserInfoBar } from './conversation-user-info-bar';
import { CurrentUser } from '@/features/users/users.types';
import { ConversationUser, ConversationAgent } from '../conversations.types';
import { useAgentState } from '../hooks/useAgentState';
import { useMessageSending } from '../hooks/useMessageSending';
import { useInfiniteMessages } from '../hooks/useInfiniteMessages';
import {
  useConversationsUpdateAgentStatus,
  useMakeConversationRead,
} from '@/features/conversations/conversations.hook';
import { useWhatsAppEvents } from '@/context/whatsapp-events-context';
import { toaster } from '@/components/ui/toaster';
import { Conversation } from '@/features/conversations/conversations.types';
import { AITakeoverConfirmationModal } from './conversation-ai-takeover-confirmation-modal';
import { Divider } from '@/components/divider';
import useBroadcast from '@/hooks/useBroadcast';

interface ConversationContainerProps {
  conversationId: string;
  conversation: Conversation;
  refreshConversations: () => void;
  currentUser: CurrentUser | null;
  isConversationLoading: boolean;
}

function ConversationContainer({
  conversationId,
  conversation,
  currentUser,
  isConversationLoading,
}: ConversationContainerProps) {
  const [isLoadScrollToBottomEnabled, setIsLoadScrollToBottomEnabled] =
    useState(false);
  const { connectChannel, isChannelConnected } = useWhatsAppEvents();

  const { mutateAsync: makeConversationRead } =
    useMakeConversationRead(conversation);

  const unreadCount = conversation.unreadCount || 0;

  const handleUpdateConversationToRead = useCallback(async () => {
    if (unreadCount > 0) {
      await makeConversationRead({
        id: conversation.id,
        data: {
          is_read: true,
        },
      });
    }
  }, [unreadCount, conversation.id, makeConversationRead]);

  // Define agents
  const aiAgent: ConversationAgent = useMemo(
    () => ({
      id: conversation?.chatbot_id || 'ai-agent-default',
      name: 'Support Agent',
      avatar: '/assets/ai-avatar.png',
    }),
    [conversation],
  );

  const humanAgent: ConversationAgent = useMemo(
    () => ({
      id: currentUser?.id || 'human-agent-1',
      name: currentUser?.fullName || 'Human Agent',
      avatar: currentUser?.avatar || '/assets/human-avatar.png',
    }),
    [currentUser],
  );

  // Custom hooks for state management
  const { activeAgentType, agentStatus } = useAgentState({ conversation });

  // Fetch messages with infinite scroll
  const {
    timeline,
    loading: messagesLoading,
    hasMore,
    fetchNextPage,
    isFetchingNextPage,
    refreshMessages,
    hasPreviousPage,
  } = useInfiniteMessages({
    conversationId,
    initialPageSize: 5,
  });

  const handleRefreshMessages = useCallback(() => {
    refreshMessages();
    handleUpdateConversationToRead();
  }, [refreshMessages, handleUpdateConversationToRead]);

  const { pendingMessages, handleMessageSent } = useMessageSending({
    refreshMessages: handleRefreshMessages,
    timeline,
  });

  const updateAgentStatusMutation = useConversationsUpdateAgentStatus({
    conversation: conversation,
    onSuccess: () => {
      refreshMessages();
    },
  });

  const {
    open: isAITakeoverOpen,
    onOpen: onAITakeoverOpen,
    onClose: onAITakeoverClose,
  } = useDisclosure();

  const {
    open: isHumanTakeoverOpen,
    onOpen: onHumanTakeoverOpen,
    onClose: onHumanTakeoverClose,
  } = useDisclosure();

  // The actual takeover function, only called after confirmation
  const handleHumanTakeover = async () => {
    if (!currentUser) return;

    // Create handover data with current user as the agent taking over
    const handoverData = {
      from_user_id: null, // No specific user is handing over (AI is)
      to_user_id: currentUser.id, // Current user is taking over
      agent_id: null, // No AI agent involved in a human takeover
      handover_reason: '(Human agent) takeover ' + 'by ' + currentUser.fullName,
    };

    // Update the conversation status to inactive (AI agent not active)
    await updateAgentStatusMutation.mutateAsync({
      conversationId,
      hasAgentActive: false,
      handoverData,
    });
    onHumanTakeoverClose();
  };

  const handleAITakeover = async () => {
    if (!currentUser) return;

    // Create handover data with AI as the new agent
    const handoverData = {
      from_user_id: currentUser.id, // Current user is handing over
      to_user_id: null, // No specific user is taking over
      // No need to set agent_id here, the updateAgentStatus function will use conversation.chatbot_id
      handover_reason: 'Handover to AI agent',
    };

    // Update the conversation status to active (AI agent active)
    await updateAgentStatusMutation.mutateAsync({
      conversationId,
      hasAgentActive: true, // has_agent_active = true (AI agent)
      handoverData,
    });

    onAITakeoverClose();
  };

  useBroadcast();

  // TODO: Need investigate why updateAgentStatusMutation.isPending broke modal if we put it direct on LoaderOverlay

  // Handle WhatsApp channel connection when conversation is selected
  useEffect(() => {
    if (!conversation?.channel_id) return;

    const connectToChannel = async () => {
      try {
        const channelId = conversation.channel_id;
        console.log('channelId', channelId);

        // Check if already connected
        if (channelId && !isChannelConnected(channelId)) {
          console.log(`Connecting to WhatsApp channel: ${channelId}`);
          await connectChannel(channelId);
          console.log(
            `Successfully connected to WhatsApp channel: ${channelId}`,
          );
          // Disable scroll to Bottom if selected channel
          setIsLoadScrollToBottomEnabled(false);
        }
      } catch (error) {
        console.error('Failed to connect to WhatsApp channel:', error);
        toaster.create({
          title: 'Connection Error',
          description: 'Failed to connect to WhatsApp channel',
          type: 'error',
          duration: 5000,
        });
      }
    };

    connectToChannel();
  }, [conversation?.channel_id, connectChannel, isChannelConnected]);

  if (!conversation || !currentUser || updateAgentStatusMutation.isPending) {
    return (
      <Center height="100%">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  // Get the current active agent based on the active type
  const currentAgent = activeAgentType === 'ai' ? aiAgent : humanAgent;

  return (
    <Box
      height="100%"
      position="relative"
      display="flex"
      flexDirection="column"
    >
      {/* <LoaderOverlay isLoading={isConversationLoading || messagesLoading} /> */}
      {/* Only render when we have both the conversation and current user */}
      {conversation && currentUser && (
        <>
          <UserInfoBar
            user={conversation.user || {}}
            conversation={conversation}
            agent={currentAgent}
            agentStatus={agentStatus}
            onHumanTakeoverOpen={onHumanTakeoverOpen}
            onAITakeoverOpen={onAITakeoverOpen}
          />
          {/* Add Divider */}
          <Divider />

          <Box flex="1" overflow="hidden" position="relative">
            <MessageThread
              conversationId={conversationId}
              user={conversation.user as ConversationUser}
              aiAgent={aiAgent}
              humanAgent={humanAgent}
              activeAgentType={activeAgentType}
              isLoading={isConversationLoading || messagesLoading}
              timeline={timeline}
              pendingMessages={pendingMessages}
              channelId={conversation.channel_id}
              onRefreshMessages={handleRefreshMessages}
              hasMore={hasMore}
              fetchNextPage={fetchNextPage}
              isFetchingNextPage={isFetchingNextPage}
              hasPreviousPage={hasPreviousPage}
              isLoadScrollToBottomEnabled={isLoadScrollToBottomEnabled}
              setIsLoadScrollToBottomEnabled={setIsLoadScrollToBottomEnabled}
              onUpdateConversationToRead={handleUpdateConversationToRead}
            />

            <MessageInput
              key={conversationId}
              conversationId={conversationId}
              conversation={conversation}
              isDisabled={agentStatus === 'active' && activeAgentType === 'ai'}
              currentUser={currentUser}
              onMessageSent={handleMessageSent}
              onTakeoverClick={onHumanTakeoverOpen}
            />
          </Box>
        </>
      )}

      {/* Human Takeover Confirmation Modal */}
      <TakeoverConfirmationModal
        isOpen={isHumanTakeoverOpen}
        onClose={onHumanTakeoverClose}
        onConfirm={handleHumanTakeover}
      />

      {/* AI Takeover Confirmation Modal */}
      <AITakeoverConfirmationModal
        isOpen={isAITakeoverOpen}
        onClose={onAITakeoverClose}
        onConfirm={handleAITakeover}
      />
    </Box>
  );
}

export default ConversationContainer;
